import type {
  ContactHandleSchema,
  JurisdictionType,
  PoliceDepartmentEnrichmentStatus,
} from '@/services/api/generated/policeDepartmentsService';
import { TimeZone } from '@/services/types/firms-types';

// Config types
interface JurisdictionTypeConfig {
  label: string;
  description: string;
  examples: string[];
  icon: string;
}

interface EnrichmentStatusConfig {
  label: string;
  shortLabel: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  description: string;
  loadingText?: string;
  successText?: string;
  actionable: boolean;
  actionText?: string;
  badgeColor: {
    bg: string;
    text: string;
    border: string;
  };
  icon: string;
}

const JURISDICTION_TYPE_CONFIG: Record<
  JurisdictionType,
  JurisdictionTypeConfig
> = {
  city: {
    label: 'City',
    description: 'Municipal police departments',
    examples: ['NYPD', 'LAPD', 'Chicago PD'],
    icon: 'building-office',
  },
  county: {
    label: 'County',
    description: 'County sheriff departments',
    examples: ['Los Angeles County Sheriff', 'Cook County Sheriff'],
    icon: 'map',
  },
  state: {
    label: 'State',
    description: 'State police and highway patrol',
    examples: ['California Highway Patrol', 'Texas State Police'],
    icon: 'globe-americas',
  },
  federal: {
    label: 'Federal',
    description: 'Federal law enforcement agencies',
    examples: ['FBI', 'DEA', 'ATF'],
    icon: 'flag',
  },
  tribal: {
    label: 'Tribal',
    description: 'Tribal police departments',
    examples: ['Navajo Nation Police', 'Cherokee Nation Police'],
    icon: 'user-group',
  },
  special: {
    label: 'Special',
    description: 'Specialized police departments',
    examples: ['Port Authority Police', 'Campus Police', 'Transit Police'],
    icon: 'shield-check',
  },
};

const ENRICHMENT_STATUS_CONFIG: Record<
  PoliceDepartmentEnrichmentStatus,
  EnrichmentStatusConfig
> = {
  not_enriched: {
    label: 'Basic Info Only',
    shortLabel: 'Basic',
    variant: 'secondary',
    description: 'We have basic department info but no contact details yet',
    loadingText: 'Basic information only',
    actionable: true,
    actionText: 'Find Contact Info',
    badgeColor: {
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      border: 'border-gray-300',
    },
    icon: 'information-circle',
  },
  enrichment_in_progress: {
    label: 'Gathering Information...',
    shortLabel: 'Enriching...',
    variant: 'outline',
    description: 'Currently finding contact information and procedures',
    loadingText: 'Finding contact details...',
    actionable: false,
    badgeColor: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      border: 'border-blue-200',
    },
    icon: 'arrow-path',
  },
  enrichment_failed: {
    label: 'Information Unavailable',
    shortLabel: 'Failed',
    variant: 'destructive',
    description: 'Unable to find additional contact information',
    loadingText: 'Information unavailable',
    actionable: true,
    actionText: 'Try Again',
    badgeColor: {
      bg: 'bg-red-50',
      text: 'text-red-600',
      border: 'border-red-200',
    },
    icon: 'exclamation-triangle',
  },
  enrichment_succeeded: {
    label: 'Enriched',
    shortLabel: 'Enriched',
    variant: 'default',
    description: 'Contact information sourced from official sources online',
    successText: 'Complete information available',
    actionable: false,
    badgeColor: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      border: 'border-green-200',
    },
    icon: 'check-circle',
  },
};

const STATE_CODE_MAP: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const TIMEZONE_LABEL_MAP: Record<TimeZone, string> = {
  [TimeZone.PST]: 'Pacific (PST)',
  [TimeZone.MST]: 'Mountain (MST)',
  [TimeZone.CST]: 'Central (CST)',
  [TimeZone.EST]: 'Eastern (EST)',
  [TimeZone.AKST]: 'Alaska (AKST)',
  [TimeZone.AST]: 'Atlantic (AST)',
  [TimeZone.HST]: 'Hawaii (HST)',
};

export const getJurisdictionTypeConfig = (
  type: JurisdictionType,
): JurisdictionTypeConfig => {
  return JURISDICTION_TYPE_CONFIG[type];
};

export const getEnrichmentStatusConfig = (
  status: PoliceDepartmentEnrichmentStatus | null | undefined,
): EnrichmentStatusConfig => {
  if (!status) {
    return ENRICHMENT_STATUS_CONFIG.not_enriched;
  }

  return (
    ENRICHMENT_STATUS_CONFIG[status] || ENRICHMENT_STATUS_CONFIG.not_enriched
  );
};

export function formatJurisdictionType(type: JurisdictionType): string {
  const config = getJurisdictionTypeConfig(type);
  return config.label;
}

export function formatEnrichmentStatus(
  status: PoliceDepartmentEnrichmentStatus,
): { label: string; color: string } {
  const config = getEnrichmentStatusConfig(status);
  return {
    label: config.label,
    color: config.badgeColor.bg,
  };
}

export function formatTimeZone(tz: TimeZone): string {
  return TIMEZONE_LABEL_MAP[tz] || tz;
}

export function getStateFullName(stateCode: string): string {
  const upperCode = stateCode.toUpperCase();
  return STATE_CODE_MAP[upperCode] || stateCode;
}

export function formatContactHandle(handle: ContactHandleSchema): string {
  const type = handle.handle_type?.toUpperCase() || 'CONTACT';
  const value = handle.value || '';

  if (!value) {
    return type;
  }

  switch (type) {
    case 'PHONE':
    case 'FAX':
      return formatPhoneNumber(value);
    case 'EMAIL':
      return value;
    case 'WEBSITE':
    case 'URL':
      return formatUrl(value);
    default:
      return `${type}: ${value}`;
  }
}

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return phone;
}

function formatUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
